/**
 * Safe DOM construction. The console is a CREDENTIALED origin - an XSS here
 * can call /auth/refresh and lift an access token - so NOTHING untrusted
 * (lot titles, hip numbers, bidder names, feed lines) is ever put through
 * innerHTML. Build nodes; text goes in as text nodes (textContent / append),
 * which the browser never parses as markup.
 *
 * There is no `html()` here on purpose: if a helper existed it would be used.
 */

// booleans/null/undefined are skipped, so `cond && el(...)` and
// `cond ? el(...) : false` both work (TS widens array `false` to boolean).
type Child = Node | string | null | undefined | boolean;
const isRenderable = (c: Child): c is Node | string => typeof c === 'string' || c instanceof Node;

export interface ElOpts {
  class?: string;
  /** Text content - a TEXT NODE, never parsed as HTML. */
  text?: string | number;
  /** data-* attributes. */
  data?: Record<string, string>;
  /** Specific safe attributes (href, type, etc.). Values are set with
   *  setAttribute - never innerHTML. */
  attrs?: Record<string, string>;
  title?: string;
}

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, opts: ElOpts = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (opts.class) e.className = opts.class;
  if (opts.text !== undefined) e.textContent = String(opts.text);
  if (opts.title !== undefined) e.title = opts.title;
  if (opts.data) for (const [k, v] of Object.entries(opts.data)) e.dataset[k] = v;
  if (opts.attrs) for (const [k, v] of Object.entries(opts.attrs)) e.setAttribute(k, v);
  for (const c of children) if (isRenderable(c)) e.append(c); // append(string) → text node
  return e;
}

/** Replace a container's children with built nodes (or a single text string). */
export function setChildren(node: HTMLElement, ...children: Child[]): void {
  node.replaceChildren(...children.filter(isRenderable));
}

/**
 * The office's one-line alert about a person (K2c), on a desk's bar: an ALERT label and the text as a TEXT NODE (the alert
 * is staff-typed, but it is shown on a credentialed origin, so it is never parsed). No alert empties the bar and hides it,
 * so the next person on screen never inherits the last one's.
 */
export function setAlert(bar: HTMLElement, alert: string | null): void {
  setChildren(bar, ...(alert === null ? [] : [el('span', { class: 'alabel', text: 'ALERT' }), ' ', el('span', { text: alert })]));
  bar.hidden = alert === null;
}
